// Visual templates for CV preview + PDF. Each template defines the CSS
// vars used by .cv-preview (in-app) and the printable HTML doc (new tab PDF).

export const CV_TEMPLATES = [
  {
    id: "classic",
    name: "Classic",
    emoji: "🖤",
    desc: "ATS-friendly, black & gray. Professional.",
    vars: {
      accent: "#18181b",
      heading: "#18181b",
      muted: "#71717a",
      ruleColor: "#18181b",
      ruleStyle: "1.5px solid",
      fontFamily: '"Georgia", "Times New Roman", serif',
      h1Weight: 700,
      h2Transform: "uppercase",
      h2Spacing: "0.08em",
    },
  },
  {
    id: "modern",
    name: "Modern",
    emoji: "🧡",
    desc: "Warm orange accent. Bold and current.",
    vars: {
      accent: "#ea580c",
      heading: "#18181b",
      muted: "#71717a",
      ruleColor: "#f97316",
      ruleStyle: "2px solid",
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
      h1Weight: 800,
      h2Transform: "uppercase",
      h2Spacing: "0.06em",
    },
  },
  {
    id: "corporate",
    name: "Corporate",
    emoji: "💙",
    desc: "Navy blue. Finance, law, consulting.",
    vars: {
      accent: "#1e40af",
      heading: "#0f172a",
      muted: "#64748b",
      ruleColor: "#1e40af",
      ruleStyle: "2px solid",
      fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
      h1Weight: 700,
      h2Transform: "uppercase",
      h2Spacing: "0.1em",
    },
  },
  {
    id: "creative",
    name: "Creative",
    emoji: "💚",
    desc: "Emerald green. Designers, freelancers.",
    vars: {
      accent: "#047857",
      heading: "#0f172a",
      muted: "#6b7280",
      ruleColor: "#10b981",
      ruleStyle: "2px solid",
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      h1Weight: 800,
      h2Transform: "uppercase",
      h2Spacing: "0.06em",
    },
  },
  {
    id: "royal",
    name: "Royal",
    emoji: "💜",
    desc: "Deep purple. Premium, elegant.",
    vars: {
      accent: "#6d28d9",
      heading: "#1e1b4b",
      muted: "#64748b",
      ruleColor: "#7c3aed",
      ruleStyle: "2px solid",
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      h1Weight: 800,
      h2Transform: "uppercase",
      h2Spacing: "0.08em",
    },
  },
  {
    id: "bold",
    name: "Bold",
    emoji: "❤️",
    desc: "Crimson red. Stand out, confident.",
    vars: {
      accent: "#b91c1c",
      heading: "#18181b",
      muted: "#71717a",
      ruleColor: "#dc2626",
      ruleStyle: "2.5px solid",
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      h1Weight: 900,
      h2Transform: "uppercase",
      h2Spacing: "0.06em",
    },
  },
  {
    id: "fresh",
    name: "Fresh",
    emoji: "🩵",
    desc: "Teal cyan. Tech, startups, modern.",
    vars: {
      accent: "#0e7490",
      heading: "#0f172a",
      muted: "#64748b",
      ruleColor: "#06b6d4",
      ruleStyle: "2px solid",
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      h1Weight: 800,
      h2Transform: "uppercase",
      h2Spacing: "0.06em",
    },
  },
  {
    id: "warm",
    name: "Warm",
    emoji: "🌸",
    desc: "Rose pink. Friendly, creative.",
    vars: {
      accent: "#be185d",
      heading: "#18181b",
      muted: "#71717a",
      ruleColor: "#ec4899",
      ruleStyle: "2px solid",
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      h1Weight: 800,
      h2Transform: "uppercase",
      h2Spacing: "0.06em",
    },
  },
];

export const DEFAULT_TEMPLATE_ID = "modern";

export const getTemplate = (id) =>
  CV_TEMPLATES.find((t) => t.id === id) || CV_TEMPLATES.find((t) => t.id === DEFAULT_TEMPLATE_ID);

/** CSS string for the in-app preview, scoped under .cv-preview. */
export const previewCss = (template) => {
  const v = template.vars;
  return `
    .cv-preview { color:${v.heading}; line-height:1.6; font-size:14px; font-family:${v.fontFamily}; }
    .cv-preview h1 { font-size:28px; margin:0 0 4px; letter-spacing:-0.02em; font-weight:${v.h1Weight}; color:${v.heading}; }
    .cv-preview h2 { font-size:14px; margin:24px 0 10px; padding-bottom:4px; border-bottom:${v.ruleStyle} ${v.ruleColor}; text-transform:${v.h2Transform}; letter-spacing:${v.h2Spacing}; color:${v.accent}; font-weight:700; }
    .cv-preview h3 { font-size:14px; margin:14px 0 4px; font-weight:700; color:${v.heading}; }
    .cv-preview em { color:${v.muted}; font-style:italic; }
    .cv-preview ul { margin:6px 0; padding-left:20px; list-style:disc; }
    .cv-preview li { margin:4px 0; }
    .cv-preview p { margin:8px 0; }
    .cv-preview hr { border: none; border-top: 1px solid #e4e4e7; margin: 14px 0; }
    .cv-preview strong { font-weight:700; }
  `;
};

/** CSS block for the printable popup doc (NOT scoped). */
export const printCss = (template) => {
  const v = template.vars;
  return `
    html, body { background: #f5f5f5; margin: 0; padding: 0; }
    body { font-family: ${v.fontFamily}; color: ${v.heading}; line-height: 1.55; font-size: 11pt; }
    .sheet { background: #fff; max-width: 800px; margin: 24px auto; padding: 56px 64px; box-shadow: 0 6px 24px rgba(0,0,0,0.08); }
    h1 { font-size: 26pt; margin: 0 0 4px; letter-spacing: -0.02em; font-weight: ${v.h1Weight}; color: ${v.heading}; }
    h2 { font-size: 13pt; margin: 22px 0 8px; padding-bottom: 4px; border-bottom: ${v.ruleStyle} ${v.ruleColor}; text-transform: ${v.h2Transform}; letter-spacing: ${v.h2Spacing}; color: ${v.accent}; font-weight: 700; }
    h3 { font-size: 11pt; margin: 14px 0 4px; font-weight: 700; color: ${v.heading}; }
    em { color: ${v.muted}; font-style: italic; }
    ul, li { margin: 4px 0; padding-left: 18px; }
    p { margin: 6px 0; }
    hr { border: none; border-top: 1px solid #e4e4e7; margin: 12px 0; }
    strong { font-weight: 700; }
    .toolbar { position: sticky; top: 0; z-index: 10; background: #18181b; color: #fff; padding: 10px 16px; display: flex; gap: 10px; align-items: center; }
    .toolbar button { background: ${v.accent}; color: #fff; border: none; padding: 8px 14px; font-weight: 700; cursor: pointer; border-radius: 4px; font-size: 13px; }
    .toolbar button.secondary { background: transparent; border: 1px solid #52525b; }
    @media print {
      .toolbar { display: none !important; }
      .sheet { box-shadow: none; margin: 0; padding: 0; max-width: 100%; }
      @page { margin: 18mm 16mm; size: A4; }
    }
  `;
};
